import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowDownAZ, MapPin, MapPinned, MoreHorizontal, Pencil, Plus, Search, Trash2, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Can } from '@/components/can'
import { EmptyState } from '@/components/empty-state'
import { Page, PageHeader } from '@/components/page-header'
import { QueryError } from '@/components/query-state'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { usePermission } from '@/hooks/use-permission'
import { countryFlag, countryName } from '@/lib/countries'
import { timeZoneLabel } from '@/lib/timezones'
import type { Location } from '@/types/api'
import { useLocations, type LocationSort } from '../api'
import { DeleteLocationDialog } from '../components/delete-location-dialog'
import { LocationSheet } from '../components/location-sheet'

export function LocationsPage() {
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<LocationSort>('name')
  const debounced = useDebouncedValue(search.trim(), 300)
  const query = useLocations({ search: debounced || undefined, sort })
  const [sheet, setSheet] = useState<{ open: boolean; location?: Location }>({ open: false })
  const [toDelete, setToDelete] = useState<Location | null>(null)
  const canManage = usePermission('locations.manage')
  const navigate = useNavigate()

  const rows = useMemo(() => query.data?.pages.flatMap((p) => p.data) ?? [], [query.data])
  const filtered = debounced.length > 0

  return (
    <Page>
      <PageHeader
        title="Locations"
        description="Physical sites where your displays live. Each location sets the local timezone for its screens."
        actions={
          <Can permission="locations.manage">
            <Button onClick={() => setSheet({ open: true })}>
              <Plus /> New location
            </Button>
          </Can>
        }
      />

      <Card className="overflow-hidden">
        <div className="flex flex-col gap-3 border-b p-3 sm:flex-row sm:items-center">
          <div className="relative flex-1 sm:max-w-sm">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, city or address…" className="pl-9" aria-label="Search locations" />
            {search && (
              <button className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground" onClick={() => setSearch('')} aria-label="Clear search">
                <X className="size-3.5" />
              </button>
            )}
          </div>
          <Select value={sort} onValueChange={(v) => setSort(v as LocationSort)}>
            <SelectTrigger className="sm:ml-auto sm:w-44" aria-label="Sort locations">
              <ArrowDownAZ className="size-4 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Name (A–Z)</SelectItem>
              <SelectItem value="-created_at">Newest first</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {query.isPending ? (
          <TableSkeleton />
        ) : query.isError ? (
          <QueryError error={query.error} onRetry={() => void query.refetch()} />
        ) : rows.length === 0 ? (
          filtered ? (
            <EmptyState icon={Search} title="No matching locations" description={`Nothing matches “${debounced}”. Try a different search.`} compact />
          ) : (
            <EmptyState
              icon={MapPinned}
              title="Add your first location"
              description="Create a location for each store, office or venue, then attach displays to it."
              action={
                canManage && (
                  <Button onClick={() => setSheet({ open: true })}>
                    <Plus /> New location
                  </Button>
                )
              }
            />
          )
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead className="hidden md:table-cell">Timezone</TableHead>
                  <TableHead className="text-right">Displays</TableHead>
                  <TableHead className="w-12">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((location) => (
                  <TableRow
                    key={location.id}
                    className="cursor-pointer"
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('button,a,[role=menuitem]')) return
                      void navigate({ to: '/locations/$locationId', params: { locationId: location.id } })
                    }}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                          <MapPin className="size-4" />
                        </div>
                        <div className="min-w-0">
                          <Link
                            to="/locations/$locationId"
                            params={{ locationId: location.id }}
                            className="block truncate font-medium outline-none hover:text-primary focus-visible:underline"
                          >
                            {location.name}
                          </Link>
                          {location.address && <p className="max-w-[18rem] truncate text-xs text-muted-foreground">{location.address}</p>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{location.city ?? '—'}</TableCell>
                    <TableCell>
                      {location.country ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span aria-hidden>{countryFlag(location.country)}</span>
                          <span className="text-muted-foreground">{countryName(location.country)}</span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden text-muted-foreground md:table-cell">{timeZoneLabel(location.timezone)}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant={location.displays_count > 0 ? 'default' : 'secondary'} className="tabular">
                        {location.displays_count}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {canManage && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${location.name}`}>
                              <MoreHorizontal />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => setSheet({ open: true, location })}>
                              <Pencil /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem variant="destructive" onSelect={() => setToDelete(location)}>
                              <Trash2 className="text-destructive" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex items-center justify-between border-t px-5 py-3 text-xs text-muted-foreground">
              <span>
                Showing {rows.length} location{rows.length === 1 ? '' : 's'}
              </span>
              {query.hasNextPage && (
                <Button variant="outline" size="sm" loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
                  Load more
                </Button>
              )}
            </div>
          </>
        )}
      </Card>

      <LocationSheet open={sheet.open} location={sheet.location} onOpenChange={(open) => setSheet((s) => ({ ...s, open }))} />
      <DeleteLocationDialog location={toDelete} onOpenChange={(o) => !o && setToDelete(null)} />
    </Page>
  )
}

function TableSkeleton() {
  return (
    <div className="divide-y">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="flex items-center gap-4 px-5 py-3.5">
          <Skeleton className="size-8 rounded-lg" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-40" />
            <Skeleton className="h-3 w-56" />
          </div>
          <Skeleton className="hidden h-3.5 w-24 sm:block" />
          <Skeleton className="hidden h-3.5 w-28 md:block" />
          <Skeleton className="h-5 w-8 rounded-full" />
        </div>
      ))}
    </div>
  )
}
